/**
 * La silueta de la mascota repetida como un patrón que se aleja del centro.
 *
 * Vivía dentro de `ProjectPlans`, que era su único usuario. Sale aquí porque ya
 * son dos —el banner de la oferta y el de una invitación al equipo— y porque los
 * dos tienen que dibujar EXACTAMENTE lo mismo: es una marca, y una marca que se
 * repite con dos implementaciones deja de serlo en cuanto una de las dos se
 * ajusta.
 *
 * Los anillos salen de `MascotPattern`, en `@webbuilder/ui`, que es donde vive el
 * único trazado de la mascota.
 *
 * ── Los ids ─────────────────────────────────────────────────────────────────
 *
 * Las máscaras de los ojos los llevaban fijos, lo cual bastaba mientras hubiera
 * un solo banner en el documento. Con dos, el segundo referenciaría la máscara
 * del primero —los ids son globales— y si el primero se desmonta, el segundo se
 * queda sin ojos. Se numeran por instancia por el mismo motivo y de la misma
 * forma que en `VelticaMascot`.
 */
import { MASCOT_OUTLINE_BASE_WIDTH, MascotRings } from '@webbuilder/ui'

/** Cuántas veces se ha montado: cada instancia necesita ids únicos. */
let instances = 0

/** Cincuenta anillos, del tamaño de la mascota hacia fuera. */
const OUTLINE_SIZES = Array.from({ length: 50 }, (_, index) => 100 + index * 40)
const OUTLINE_OPACITY = (index: number) => Math.max(0.08, 0.58 - index * 0.009)

/**
 * Sólo la onda: los anillos grandes, sin los pequeños del centro.
 *
 * En las tarjetas de venta el centro cae detrás del botón, y ahí los primeros
 * anillos —los que todavía tienen el tamaño de la mascota— se leen como una
 * mascota metida debajo de la acción. A partir de este tamaño la silueta ya no
 * se reconoce: lo que queda son curvas concéntricas entrando por la derecha,
 * que es lo que hace de fondo.
 *
 * Se empieza en 620 y no en 100 por eso, y el paso sube a 60 para que la
 * densidad de líneas se parezca a la de siempre con menos anillos.
 */
export const MASCOT_WAVE_SIZES = Array.from({ length: 40 }, (_, index) => 620 + index * 60)

/**
 * Y el centro, en el borde derecho del lienzo.
 *
 * El de por defecto —1040 de 1200— queda a 160 unidades del borde, que en un
 * banner ancho es un pelo hacia dentro y en uno alto es el medio: `slice`
 * escala por el lado que falte para cubrir, así que cuanto más alta es la
 * tarjeta más se mete el centro hacia el centro. En 1200 cae SIEMPRE en el
 * borde, y las ondas entran por la derecha a cualquier proporción.
 */
export const MASCOT_WAVE_CENTER: [number, number] = [1200, 160]

export function MascotOutlinePattern({
  className = 'dash-plans-banner-pattern',
  eyesClassName = 'dash-plans-banner-pattern-eyes',
  /** Dónde cae el centro de los anillos dentro del lienzo de 1200 × 320. */
  center = [1040, 160],
  viewBox = '0 0 1200 320',
  preserveAspectRatio = 'xMaxYMid slice',
  sizes = OUTLINE_SIZES,
  opacity = OUTLINE_OPACITY,
  strokeWidth = 2,
  motion,
  /**
   * Si el centro lleva los ojos.
   *
   * Con ellos el patrón es una mascota mirando; sin ellos, sólo la onda de su
   * silueta. La diferencia importa cuando encima del centro hay algo: los ojos
   * son las dos únicas formas RELLENAS del dibujo, así que detrás de un botón de
   * contorno se ven a través y lo dejan pareciendo roto. Los anillos, al ser
   * trazo fino, pasan por detrás sin llamar la atención.
   */
  eyes = true,
}: {
  className?: string
  eyesClassName?: string
  center?: [number, number]
  /** Permite conservar la silueta en piezas verticales sin estirar el banner. */
  viewBox?: string
  preserveAspectRatio?: string
  sizes?: number[]
  opacity?: (index: number) => number
  strokeWidth?: number
  motion?: { step: number; durationMs: number; className: string }
  eyes?: boolean
}) {
  const uid = `mop${(instances = (instances + 1) % 1e6)}`
  const [cx, cy] = center
  /* Los ojos no van centrados en el anillo: la mascota los tiene un pelo arriba
     y a la izquierda de su centro geométrico. */
  const eyeX = cx - 7
  const eyeY = cy - 6

  return (
    <svg
      className={className}
      viewBox={viewBox}
      preserveAspectRatio={preserveAspectRatio}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <mask id={`${uid}-left-eye`} maskUnits="userSpaceOnUse" x="38" y="46" width="17" height="27">
          <rect x="39.6" y="48" width="13.2" height="22.8" rx="6.6" fill="#fff" />
          <circle cx="46.2" cy="76.5" r="8.76" fill="#000" />
        </mask>
        <mask id={`${uid}-right-eye`} maskUnits="userSpaceOnUse" x="66" y="46" width="17" height="27">
          <rect x="67.2" y="48" width="13.2" height="22.8" rx="6.6" fill="#fff" />
          <circle cx="73.8" cy="76.5" r="8.76" fill="#000" />
        </mask>
      </defs>

      <MascotRings
        center={[cx, cy]}
        sizes={sizes}
        opacity={opacity}
        strokeWidth={strokeWidth}
        /* Se pasa sólo si lo hay: con `exactOptionalPropertyTypes`, «la propiedad
           vale undefined» y «no está la propiedad» no son lo mismo, y el destino
           la declara opcional. Es el mismo patrón que el resto del repo. */
        {...(motion ? { motion } : {})}
      />

      {eyes ? (
        <g
          className={eyesClassName}
          transform={`translate(${eyeX} ${eyeY}) scale(${100 / MASCOT_OUTLINE_BASE_WIDTH}) translate(-60 -60)`}
        >
          <rect x="39.6" y="48" width="13.2" height="22.8" rx="6.6" mask={`url(#${uid}-left-eye)`} />
          <rect x="67.2" y="48" width="13.2" height="22.8" rx="6.6" mask={`url(#${uid}-right-eye)`} />
        </g>
      ) : null}
    </svg>
  )
}
